import { ResponseArray } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import qs from 'query-string'
import {
    InvoiceDetailParams,
    InvoiceDetailResponse,
    InvoiceRevenueAllocation,
    InvoicesListParams,
    InvoicesListResponse,
    MonthlyRevenueSummaryParams,
    MonthlyRevenueSummaryResponse,
    RevenueAllocationsParams,
    RevenueByPaymentDateParams,
    RevenueByPaymentDateResponse,
    RevenueCombinedByCustomerResponse,
    RevenueCombinedResponse,
    RevenueCost,
    RevenueCostParams,
    RevenueSummaryResponse,
} from '../types'

const BASE_URL = '/reports/bi'

/**
 * Get list of invoices from BI system with optional filters
 */
export const getInvoicesList = async (
    params?: InvoicesListParams
): Promise<InvoicesListResponse> => {
    const { data } = await datacentral.get(`${BASE_URL}/invoices`, { params })
    return data.result
}

/**
 * Get detailed information about a specific invoice
 */
export const getInvoiceDetail = async (
    params: InvoiceDetailParams
): Promise<InvoiceDetailResponse> => {
    const { data } = await datacentral.get(`${BASE_URL}/invoices/detail`, {
        params,
    })
    return data.result
}

/**
 * Get revenue allocation data with filters and pagination
 */
export const getRevenueCombined = async (
    params?: RevenueAllocationsParams
): Promise<RevenueCombinedResponse> => {
    const queryString = qs.stringify(params || {})
    const { data } = await datacentral.get(
        `${BASE_URL}/revenue-combined?${queryString}`
    )
    return data?.result || { results: [], pagination: null, summary: null }
}

/**
 * Get combined revenue grouped by customer with expandable items
 */
export const getRevenueCombinedByCustomer = async (
    params?: RevenueAllocationsParams
): Promise<RevenueCombinedByCustomerResponse> => {
    const queryString = qs.stringify(params || {})
    const { data } = await datacentral.get(
        `${BASE_URL}/revenue-combined-by-customer?${queryString}`
    )
    return data || { results: [], pagination: null, summary: null }
}

/**
 * Get aggregated revenue summary grouped by month, product group, and type
 */
export const getRevenueSummary = async (
    params?: RevenueAllocationsParams
): Promise<RevenueSummaryResponse> => {
    const { data } = await datacentral.get(`${BASE_URL}/revenue-summary`, {
        params,
    })
    return data.result
}

/**
 * Get high-level monthly revenue summary (aggregated across all products)
 */
export const getMonthlyRevenueSummary = async (
    params: MonthlyRevenueSummaryParams
): Promise<MonthlyRevenueSummaryResponse> => {
    const { data } = await datacentral.get(`${BASE_URL}/revenue-monthly`, {
        params,
    })
    return data.result
}

/**
 * Get revenue cost (COGS - Cost of Goods Sold) data
 */
export const getRevenueCost = async (
    params?: RevenueCostParams
): Promise<RevenueCost[]> => {
    const queryString = qs.stringify(params || {})
    const { data } = await datacentral.get<ResponseArray<RevenueCost>>(
        `${BASE_URL}/service-costs?${queryString}`
    )
    return data?.results || []
}

/**
 * Get revenue allocation preview for a specific invoice
 */
export const getInvoiceRevenueAllocation = async (invoice_id: number) => {
    const response = await datacentral.get<
        ResponseArray<InvoiceRevenueAllocation>
    >(`${BASE_URL}/invoices/${invoice_id}`)
    return response?.data?.results || []
}

/**
 * Reallocate revenue for invoice items (manual override)
 */
export const reallocateInvoiceRevenue = async (payload: {
    invoice_id: number
    invoice_items: Array<{
        invoice_item_id: number
        revenue_allocations: Array<{
            revenue_month: string // YYYY-MM
            allocated_amount: number
        }>
    }>
}) => {
    const { data } = await datacentral.post(
        `${BASE_URL}/invoices/reallocate`,
        payload
    )
    return data
}

/**
 * Get revenue by invoice payment date (cash flow analysis)
 * Only returns actual paid allocations
 */
export const getRevenueByPaymentDate = async (
    params?: RevenueByPaymentDateParams
): Promise<RevenueByPaymentDateResponse> => {
    const queryString = qs.stringify(params || {})
    const { data } = await datacentral.get(
        `${BASE_URL}/revenue-by-payment-date?${queryString}`
    )
    return data || { results: [], pagination: null, summary: null }
}

// Re-export overview & export functions
export {
    exportRevenueCombined,
    getRevenueOverview,
    getPaymentDateOverview,
    compareAccountingRevenue,
} from './revenue-overview'
